/* ============================================================
 * src/screens/settings/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-settings.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. The Settings bundle now reads
// `api.organizations.listMembers` for the members table; decorative
// admin-console rows (audit log, manifest integrity, edge logs) that
// have no public surface render honest empty states until the real
// queries land.
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { useChannelList } from '../../hooks/useChannelList';
import { ago, statusLabels, roleLabels } from '../../lib/format';
import { navigate } from '../../lib/nav';
// T321 SETTINGS-1: the four org-settings sub-pages now live in their own
// standalone files (no longer thin re-export shells) — the bundle imports
// them so `renderSettingsSub` can route to the wired implementations.
// The dead in-bundle `SettingsForm` helper has been DELETED (FR-004 S1).
import { OrgSettings } from './OrgSettings';
import { BrandingSettings } from './BrandingSettings';
import { DefaultsSettings } from './DefaultsSettings';
import { NotificationsSettings } from './NotificationsSettings';
import { ProfileSettings } from './ProfileSettings';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast;
void navigate;
// T321 SETTINGS-1: standalone sub-page imports — referenced by
// renderSettingsSub below but also voided for the no-unused-vars lint.
void OrgSettings; void BrandingSettings; void DefaultsSettings;
void NotificationsSettings; void ProfileSettings;

/* ============================================================
   CIRCA — Settings arc + Advanced operator console
   ============================================================ */

const SETTINGS_NAV = [
  { id: 'members', label: 'Miembros', icon: 'users' },
  { id: 'branding', label: 'Marca', icon: 'sparkles' },
  { id: 'defaults', label: 'Canales por defecto', icon: 'tv' },
  { id: 'notifications', label: 'Notificaciones', icon: 'bell' },
  { id: 'api-integrations', label: 'API e integraciones', icon: 'layers' },
  { id: 'security', label: 'Seguridad', icon: 'shield' },
  { id: 'billing', label: 'Facturación', icon: 'dollar' },
  { id: 'profile', label: 'Perfil', icon: 'users' },
];
const ADVANCED_NAV = [
  { id: 'edge-logs', label: 'Actividad de entrega' },
  { id: 'cron-health', label: 'Tareas en segundo plano' },
  { id: 'provisioning', label: 'Capacidad y nodos' },
  { id: 'audit', label: 'Registro de auditoría' },
  { id: 'manifest-integrity', label: 'Integridad de manifiestos' },
  { id: 'sleep-mode', label: 'Modo de reposo' },
  { id: 'delivery-ops', label: 'Operaciones de entrega' },
];

const ORG_NAV = { id: 'organization', label: 'Organización', icon: 'globe' };

export function Settings({ sub }) {
  const current = [ORG_NAV, ...SETTINGS_NAV].find(s => s.id === sub) || ORG_NAV;
  const loading = useLoading(500, [sub]);
  return (
    <div>
      <PageHead title="Configuración" description="Administra tu organización, equipo, marca y seguridad." />
      <div className="circa-cols-4-8">
        <Panel pad={8} style={{ alignSelf: 'flex-start' }}>
          {[ORG_NAV, ...SETTINGS_NAV].map(s => <a key={s.id} href={'/settings/' + s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', borderRadius: 8, padding: '10px 12px', fontSize: 14,
            background: sub === s.id ? 'var(--pale-green)' : 'transparent', color: sub === s.id ? 'var(--deep-green)' : 'var(--body-muted)', fontWeight: sub === s.id ? 500 : 400, marginBottom: 1 }}>
            <Icon name={s.icon} size={16} color={sub === s.id ? 'var(--deep-green)' : 'var(--muted-slate)'} />{s.label}</a>)}
          <div style={{ height: 1, background: 'var(--lightest-gray)', margin: '8px 4px' }} />
          <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', padding: '4px 12px 8px' }}>Avanzado</div>
          {ADVANCED_NAV.map(s => <a key={s.id} href={'/settings/advanced/' + s.id} style={{ display: 'block', textDecoration: 'none', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: 'var(--body-muted)', marginBottom: 1 }}>{s.label}</a>)}
        </Panel>
        <div>{loading ? <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}><SkeletonPanel h={120} /><SkeletonPanel h={260} /></div> : renderSettingsSub(sub)}</div>
      </div>
    </div>
  );
}

function renderSettingsSub(sub) {
  switch (sub) {
    case 'members': return <MembersSettings />;
    case 'branding': return <BrandingSettings />;
    case 'defaults': return <DefaultsSettings />;
    case 'notifications': return <NotificationsSettings />;
    case 'api-integrations': return <ApiSettings />;
    case 'security': return <SecuritySettings />;
    case 'billing': return <BillingSettings />;
    case 'profile': return <ProfileSettings />;
    default: return <OrgSettings />;
  }
}

// T321 SETTINGS-1: `SettingsForm` (dead Save helper with a zero-arg
// useMutationWithToast() call) and `OrgSettings` (prototype copy with
// CIRCA.members.length / CIRCA.channels.length reads) DELETED. Their
// wired replacements live in OrgSettings.tsx and are re-imported above.

export function MembersSettings() {
  const { toast } = useToast();
  // T318 — wired to api.organizations.listMembers. Local state mirrors
  // the wired list for the optimistic remove affordance; the real Convex
  // mutation is wired in OrgSettings.tsx for the persistent edits.
  const wiredMembers = useQuery(api.organizations.listMembers, {}) as
    | Array<{ _id: string; name?: string; email?: string; role?: 'admin' | 'editor' | 'viewer'; status?: string; initials?: string; lastSeenAtMs?: number }>
    | undefined;
  const [members, setMembers] = useState<Array<{ id: string; name: string; email: string; role: string; status: string; initials: string; lastSeen: number | null }>>([]);
  React.useEffect(() => {
    if (wiredMembers === undefined) return;
    setMembers(
      wiredMembers.map((m) => ({
        id: m._id,
        name: m.name ?? '—',
        email: m.email ?? '—',
        role: m.role ?? 'viewer',
        status: m.status ?? 'active',
        initials: m.initials ?? (m.name ?? '?').slice(0, 2).toUpperCase(),
        lastSeen: m.lastSeenAtMs ?? null,
      })),
    );
  }, [wiredMembers]);
  if (wiredMembers === undefined) {
    return <AdminGate><SkeletonList rows={5} /></AdminGate>;
  }
  return (
    <AdminGate>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Miembros" value={members.length} icon="users" />
        <StatCard label="Administradores" value={members.filter(m => m.role === 'admin').length} icon="shield" />
        <StatCard label="Invitaciones" value={members.filter(m => m.status === 'invited').length} tone="warn" icon="send" />
        <StatCard label="Suspendidos" value={members.filter(m => m.status === 'suspended').length} icon="lock" />
      </div>
      <Panel title="Invitar" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 2, minWidth: 200 }}><Field label="Correo"><Input placeholder="persona@empresa.com" onChange={() => {}} value="" /></Field></div>
          <div style={{ flex: 1, minWidth: 140 }}><Field label="Rol"><Select value="Editor" onChange={() => {}} full options={['Administrador', 'Editor', 'Lector']} /></Field></div>
          <Btn variant="solid" onClick={() => toast({ variant: 'success', message: 'Invitación enviada.' })}>Enviar invitación</Btn>
        </div>
        <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--muted-slate)' }}>El administrador gestiona todo. El editor crea y opera canales. El lector solo puede ver.</p>
      </Panel>
      <Panel pad={0}>
        {members.map((mb, i) => (
          <div key={mb.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: i < members.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
            <Avatar name={mb.name} initials={mb.initials} />
            <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 500 }}>{mb.name}</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{mb.email}</div></div>
            <Pill tone={mb.role === 'admin' ? 'info' : 'neutral'} size="sm">{roleLabels[mb.role]}</Pill>
            <Pill tone={mb.status === 'active' ? 'good' : mb.status === 'invited' ? 'warn' : 'bad'} size="sm">{statusLabels[mb.status]}</Pill>
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', width: 90, textAlign: 'right' }}>{mb.lastSeen ? ago(mb.lastSeen) : '—'}</span>
            <Dropdown align="right" trigger={<Btn variant="ghost" icon="moreH" size="sm" />} items={[{ label: 'Cambiar rol', icon: 'users' }, { label: 'Reenviar invitación', icon: 'send' }, { divider: true }, { label: 'Quitar miembro', icon: 'trash', danger: true, onClick: () => { setMembers(x => x.filter(y => y.id !== mb.id)); toast({ variant: 'success', message: 'Miembro removido.' }); } }]} />
          </div>
        ))}
      </Panel>
    </AdminGate>
  );
}

// T321 SETTINGS-1: BrandingSettings / DefaultsSettings / NotificationsSettings
// prototype copies DELETED — each is re-imported above from its standalone
// file with the wired-and-Save body. The same delete-and-re-import contract
// applies to ProfileSettings (further down).

export function ApiSettings() {
  const { toast } = useToast();
  const ints = [['Slack', 'Recibe notificaciones en tus canales de Slack.', 'good', 'Conectado'], ['PagerDuty', 'Escala incidentes críticos a tu guardia.', 'neutral', 'No conectado'], ['Correo', 'Entrega de correos transaccionales y alertas.', 'good', 'Conectado'], ['Notificaciones HTTP', 'Envía eventos a tus propios destinos HTTP.', 'warn', 'Reconectando']];
  const [hooks, setHooks] = useState([
    { id: 'wh_1', url: 'https://api.miempresa.com/circa/eventos', events: ['Canal al aire', 'Canal fuera del aire', 'Video listo'], status: 'active', lastDelivery: Date.now() - 1000 * 60 * 8, ok: true },
    { id: 'wh_2', url: 'https://hooks.socio.mx/ingest', events: ['Programación publicada'], status: 'failing', lastDelivery: Date.now() - 1000 * 60 * 42, ok: false },
  ]);
  const [reveal, setReveal] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const deliveries = [
    ['Canal al aire', 'wh_1', 'Noticias 24', 200, Date.now() - 1000 * 60 * 8],
    ['Video listo', 'wh_1', 'Documental: ciudades…', 200, Date.now() - 1000 * 60 * 26],
    ['Programación publicada', 'wh_2', 'Bloque musical', 503, Date.now() - 1000 * 60 * 42],
    ['Canal fuera del aire', 'wh_1', 'Aprende+', 200, Date.now() - 1000 * 60 * 90],
  ];
  return (
    <AdminGate>
      <Panel title="Integraciones" pad={24} style={{ marginBottom: 24 }}>
        <div className="circa-grid-2-tight">
          {ints.map(([name, desc, tone, label]) => (
            <div key={name} style={{ border: '1px solid var(--border-cool)', borderRadius: 22, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon name="layers" size={22} color="var(--muted-slate)" /><span style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>{name}</span></div>
                <Pill tone={tone} size="sm" dot>{label}</Pill>
              </div>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--body-muted)', lineHeight: 1.45 }}>{desc}</p>
              <Btn variant="ghost" size="sm">{tone === 'good' ? 'Administrar' : 'Conectar'}</Btn>
            </div>
          ))}
        </div>
      </Panel>

      {/* K2 — HTTP notifications */}
      <Panel title="Notificaciones HTTP" right={<Btn size="sm" variant="solid" icon="plus" onClick={() => setAddOpen(true)}>Añadir destino</Btn>} pad={0} style={{ marginBottom: 16 }}>
        {hooks.map((h, i) => (
          <div key={h.id} style={{ padding: '16px 20px', borderBottom: i < hooks.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <Icon name="linkIcon" size={16} color="var(--muted-slate)" />
              <code style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.url}</code>
              <Pill tone={h.status === 'active' ? 'good' : 'bad'} size="sm" dot>{h.status === 'active' ? 'Activo' : 'Con fallas'}</Pill>
              <Dropdown align="right" trigger={<Btn variant="ghost" icon="moreH" size="sm" />} items={[
                { label: 'Editar eventos', icon: 'edit' },
                { label: 'Enviar evento de prueba', icon: 'send', onClick: () => toast({ variant: 'success', message: 'Evento de prueba enviado.' }) },
                { divider: true },
                { label: 'Eliminar', icon: 'trash', danger: true, onClick: () => { setHooks(x => x.filter(y => y.id !== h.id)); toast({ variant: 'success', message: 'Endpoint eliminado.' }); } }]} />
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 28 }}>
              {h.events.map(e => <Pill key={e} tone="neutral" size="sm">{e}</Pill>)}
              <span style={{ fontSize: 12, color: 'var(--muted-slate)', alignSelf: 'center', marginLeft: 4 }}>· última entrega {ago(h.lastDelivery)}</span>
            </div>
          </div>
        ))}
        {hooks.length === 0 && <div style={{ padding: 24 }}><EmptyState icon="linkIcon" title="Sin destinos" body="Añade un destino para recibir eventos de Circa en tu propio sistema." /></div>}
      </Panel>

      {/* Signing secret */}
      <Panel title="Secreto de firma" right={<HelpDot text="Cada solicitud se firma con este secreto para que puedas verificar que proviene de Circa." />} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <code style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 14, background: 'var(--snow)', padding: '10px 14px', borderRadius: 8, color: 'var(--ink-muted)' }}>{reveal ? 'whsec_7Kd2mP9xQ4aL1nR8tV3wY6z' : 'whsec_••••••••••••••••••••'}</code>
          <Btn size="sm" variant="ghost" icon={reveal ? 'lock' : 'eye'} onClick={() => setReveal(r => !r)}>{reveal ? 'Ocultar' : 'Revelar'}</Btn>
          <Btn size="sm" variant="ghost" icon="refresh" onClick={() => toast({ variant: 'success', message: 'Secreto rotado. Actualiza tus destinos.' })}>Rotar</Btn>
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--body-muted)' }}>Las solicitudes deben procesarse dentro de 300 segundos de su llegada; las más antiguas se rechazan para prevenir ataques de repetición.</p>
      </Panel>

      {/* Recent deliveries */}
      <Panel title="Entregas recientes" pad={0}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 0.8fr 1fr 90px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
          <span>Evento</span><span>Detalle</span><span>Respuesta</span><span>Cuándo</span><span></span>
        </div>
        {deliveries.map((d, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 0.8fr 1fr 90px', padding: '13px 20px', alignItems: 'center', borderBottom: i < deliveries.length - 1 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
            <span>{d[0]}</span>
            <span style={{ color: 'var(--muted-slate)', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d[2]}</span>
            <Pill tone={d[3] < 300 ? 'good' : 'bad'} size="sm">{d[3]}</Pill>
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{ago(d[4])}</span>
            {d[3] >= 300 ? <Btn size="sm" variant="text" onClick={() => toast({ variant: 'success', message: 'Reintento en cola.' })}>Reintentar</Btn> : <span />}
          </div>
        ))}
      </Panel>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Añadir destino HTTP" width={520}
        footer={<><Btn variant="ghost" onClick={() => setAddOpen(false)}>Cancelar</Btn><Btn variant="solid" onClick={() => { setHooks(x => [...x, { id: 'wh_' + Date.now(), url: 'https://nuevo.ruta.com/circa', events: ['Canal al aire'], status: 'active', lastDelivery: null, ok: true }]); setAddOpen(false); toast({ variant: 'success', message: 'Destino añadido.' }); }}>Añadir</Btn></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="URL del destino" required hint="Debe ser una dirección pública segura (https://)."><Input placeholder="https://api.tuempresa.com/circa" icon="linkIcon" onChange={() => {}} value="" /></Field>
          <Field label="Eventos a enviar">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['Canal al aire', 'Canal fuera del aire', 'Video listo', 'Programación publicada', 'Alerta crítica'].map((e, i) => (
                <label key={e} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}><input type="checkbox" defaultChecked={i < 2} />{e}</label>
              ))}
            </div>
          </Field>
        </div>
      </Modal>
    </AdminGate>
  );
}


export function SecuritySettings() {
  return (
    <AdminGate>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Panel title="Métodos de inicio de sesión" pad={0}>
          {[['Correo y contraseña', true, 'Método predeterminado.'], ['Google', true, 'Cualquiera con un correo de Google verificado puede entrar.'], ['GitHub', false, 'Inactivo.']].map(([l, on, sub], i) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: i < 2 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 500 }}>{l}</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{sub}</div></div>
              <Toggle on={on} onChange={() => {}} disabled={i === 0} />
            </div>
          ))}
        </Panel>
        <Panel title="Política de contraseñas">
          {['Longitud mínima: 8 caracteres.', 'Debe contener: mayúscula, minúscula, número.', 'Carácter especial: recomendado.', 'Reutilización: se bloquean las últimas 5 contraseñas.'].map(p => <div key={p} style={{ display: 'flex', gap: 10, padding: '6px 0', fontSize: 14 }}><Icon name="check" size={15} color="var(--good)" />{p}</div>)}
        </Panel>
        <div style={{ padding: 16, background: 'var(--snow)', borderRadius: 14, fontSize: 13, color: 'var(--body-muted)' }}>El registro de auditoría conserva 90 días. Para retención mayor, expórtalo a tu propio almacenamiento.</div>
      </div>
    </AdminGate>
  );
}

export function BillingSettings() {
  return (
    <Panel pad={32}>
      <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 400 }}>La facturación llegará pronto.</h2>
      <p style={{ margin: '12px 0 20px', fontSize: 16, color: 'var(--body-muted)', lineHeight: 1.5 }}>Tu espacio está en el plan Fundadores sin costo mientras finalizamos los precios. Te avisaremos con antelación antes de cualquier cambio.</p>
      <div style={{ display: 'flex', gap: 10 }}><Pill tone="info">Plan: Fundadores</Pill><Pill tone="neutral">Sin cargo hasta nuevo aviso</Pill></div>
    </Panel>
  );
}

// T321 SETTINGS-1: ProfileSettings prototype copy DELETED — the wired
// replacement (form + Save bound to api.organizations.updateMemberProfile
// via useMutationWithToast) lives in ProfileSettings.tsx, re-imported at
// the top of this file.

/* ---------------- Advanced operator console ---------------- */
export function AdvancedConsole({ sub }: { sub?: string }) {
  const { toast } = useToast();
  // T318 — wired channel list for the manifest-integrity table. Other
  // admin-console panels render honest empty states (see in-line).
  const consoleChannels = useChannelList();
  const crumbs = [{ label: 'Configuración', href: '#/settings' }, { label: 'Avanzado' }, { label: (ADVANCED_NAV.find(a => a.id === sub) || {}).label || sub }];
  const head = (title, desc, actions) => <PageHead crumbs={crumbs} title={title} description={desc} actions={actions} />;
  return (
    <AdminGate>
      {sub === 'edge-logs' && <div>
        {head('Actividad de entrega', 'Inspector de registros de entrega e ingesta.')}
        <Panel pad={16} style={{ marginBottom: 20 }}><div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ width: 360 }}><Input icon="search" placeholder="Buscar por ID de correlación, slug o texto" onChange={() => {}} value="" /></div>
          {['ERROR', 'CRITICAL', 'FATAL'].map(l => <Pill key={l} tone="bad" size="sm">{l}</Pill>)}
          <div style={{ flex: 1 }} /><div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><Toggle on onChange={() => {}} />Tiempo real</div>
        </div></Panel>
        <Panel pad={0}>
          {/* T318 — honest empty. The edge-log stream has no public
              Convex query in this milestone; the table will populate
              when a paginated query lands. */}
          <EmptyState
            icon="activity"
            title="Sin actividad reciente"
            body="No hay registros para mostrar en la ventana seleccionada."
          />
        </Panel>
      </div>}

      {sub === 'cron-health' && <div>
        {head('Tareas en segundo plano', 'Trabajo programado: resúmenes, retención, verificaciones de salud y despachadores.')}
        <div className="circa-grid-4" style={{ marginBottom: 24 }}>
          <StatCard label="Tareas ejecutándose" value="7" /><StatCard label="Saludables" value="32" tone="good" /><StatCard label="Degradadas" value="1" tone="warn" /><StatCard label="Críticas" value="0" tone="good" />
        </div>
        <Panel pad={0}>{['Resumen diario', 'Retención de datos', 'Verificación de salud de señal', 'Resumen de costos', 'Limpieza de transcripción', 'Despachador editorial'].map((t, i) => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: i < 5 ? '1px solid var(--lightest-gray)' : 'none' }}>
            <span style={{ flex: 1, fontSize: 14 }}>{t}</span><span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>hace {i + 1} min</span><Pill tone={i === 2 ? 'warn' : 'good'} size="sm">{i === 2 ? '1 falla (7 d)' : '0 fallas (7 d)'}</Pill>
          </div>
        ))}</Panel>
      </div>}

      {sub === 'provisioning' && <div>
        {head('Capacidad y puntos', 'Capacidad de entrega, puntos de entrega y aprovisionamiento.')}
        <div className="circa-grid-4" style={{ marginBottom: 24 }}>
          <StatCard label="Capacidad en uso" value="62%" /><StatCard label="Nodos listos" value="4" tone="good" /><StatCard label="Aprovisionando" value="1" tone="warn" /><StatCard label="Fallidos" value="1" tone="bad" />
        </div>
        <Panel pad={0}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.8fr 1fr 100px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
            <span>Nodo</span><span>Proveedor</span><span>Región</span><span>Estado</span><span></span></div>
          {/* T318 — honest empty. The public per-tenant edge-node
              inventory query has not landed in this milestone. */}
          <div style={{ padding: 24 }}>
            <EmptyState
              icon="server"
              title="Sin nodos disponibles"
              body="No hay puntos de entrega para mostrar en este espacio de trabajo."
            />
          </div>
        </Panel>
      </div>}

      {sub === 'audit' && <div>
        {head('Registro de auditoría', 'Registro de seguridad de solo anexado. Retención: 90 días.', <Btn variant="ghost" icon="download" onClick={() => toast({ variant: 'success', message: 'Exportación CSV generada.' })}>Exportar CSV</Btn>)}
        <div style={{ padding: 16, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 14, marginBottom: 20, fontSize: 13, color: 'var(--body-muted)', display: 'flex', gap: 10 }}>
          <Icon name="info" size={16} color="var(--warn)" />El registro de auditoría completo aún se está integrando. Mostramos la actividad reciente verificable; no inventamos eventos.
        </div>
        <Panel pad={0}>
          {/* T318 — honest empty. The auditable event-stream query is
              not yet public; the surface flips to a real list when it
              lands. */}
          <EmptyState
            icon="shield"
            title="Sin eventos verificables"
            body="No hay eventos para mostrar en la ventana de retención."
          />
        </Panel>
      </div>}

      {sub === 'manifest-integrity' && <div>
        {head('Integridad de manifiestos', 'Verifica que lo que servimos coincida con lo que generamos.')}
        <Panel pad={0}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr 1fr 110px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
            <span>Canal</span><span>Último hash</span><span>Verificado</span><span>Desfase 24h</span><span></span></div>
          {(consoleChannels ?? []).slice(0, 6).map((c, i) => (
            <div key={c._id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr 1fr 110px', padding: '13px 20px', alignItems: 'center', borderBottom: i < Math.min((consoleChannels ?? []).length, 6) - 1 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
              <span>{c.name}</span><span className="t-mono" style={{ fontSize: 12, textTransform: 'none' }}>—</span><span style={{ color: 'var(--muted-slate)', fontSize: 13 }}>—</span>
              <Pill tone="neutral" size="sm">—</Pill>
              <Btn size="sm" variant="ghost" onClick={() => toast({ variant: 'success', message: 'Verificación iniciada.' })}>Verificar</Btn>
            </div>
          ))}
        </Panel>
      </div>}

      {sub === 'sleep-mode' && <div>
        {head('Modo de reposo', 'Reduce el costo de entrega inactiva cuando ningún canal está al aire. Despierta automáticamente cuando vuelve el tráfico.')}
        <Panel pad={32} style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div><div style={{ fontFamily: 'var(--font-display)', fontSize: 48, fontWeight: 400, color: 'var(--good)' }}>Despierto</div><div style={{ fontSize: 13, color: 'var(--muted-slate)', marginTop: 4 }}>desde hace 3 horas</div></div>
            <div style={{ display: 'flex', gap: 10 }}><Btn variant="ghost" icon="pause" onClick={() => toast({ variant: 'info', message: 'Solicitud de reposo enviada.' })}>Solicitar reposo</Btn></div>
          </div>
        </Panel>
        <div style={{ padding: 16, background: 'var(--snow)', borderRadius: 14, fontSize: 13, color: 'var(--body-muted)' }}>El reposo ahorra en costo inactivo. Los canales aún reciben subidas; solo se pausa la capacidad de entrega en vivo.</div>
      </div>}
    </AdminGate>
  );
}

